import * as React from "react";
import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { Loader2, X } from "lucide-react";

import { cn } from "../lib/cn";
import { formatRelativePast } from "../lib/relativeTime";

/**
 * ActiveSyncJobBanner — sticky info surface telling the operator a
 * sync job is already running for the current connection, with a
 * single-click escape hatch to cancel it.
 *
 * Lives in ``@ando/ui`` (DRY anchor) so the modal AND the future
 * Integrazioni hub list row can render the SAME chip without forking
 * the styling. Per the June 10, 2026 user feedback: "Quando c'è un
 * sync già attivo non viene gestito dalla v2!! Va gestito e fatto
 * vedere ed eventualmente ci vuole stop come già avviene in v1".
 *
 * Visual + a11y contract:
 *   - Outer container is a ``role="status"`` live region
 *     (``aria-live="polite"`` + ``aria-atomic``) so screen readers
 *     announce both the initial "Sincronizzazione già in corso" AND
 *     the transition to the ``"cancelling"`` state when the operator
 *     clicks Annulla. The CTA itself sits OUTSIDE the live region so
 *     interaction state (disabled / loading) doesn't double-announce.
 *   - Auto-focuses on mount via a ``ref + tabIndex=-1`` pattern so
 *     keyboard users land on the recovery surface immediately when
 *     the parent renders us — the previously-focused "Sincronizza"
 *     button is now disabled and would otherwise leave the operator
 *     stranded with no obvious next action.
 *   - Soft-orange tint (ando-orange-soft) — the running-state surface
 *     used elsewhere in v2 for in-progress sync chips. Carries a thin
 *     pulse on the leading dot so the operator's eye picks it up at
 *     a glance without becoming nagging. ``motion-reduce:animate-none``
 *     respects the OS-level reduced-motion preference.
 *   - Animated spinner alongside the label — reads as "still doing
 *     work" instead of "stuck". Stops + flips to ``X`` icon when the
 *     parent surfaces a TERMINAL state (e.g. cancel completed).
 *   - Relative timestamp via the shared
 *     :func:`@ando/ui/lib/relativeTime.formatRelativePast` helper —
 *     single DRY anchor for every banner/chip across the SPA.
 */

export type ActiveSyncJobBannerStatus =
  /** BE 409 just fired — sync is running, no cancel in flight. */
  | "running"
  /** Operator clicked Annulla; waiting for BE to ack the cancel. */
  | "cancelling";

export interface ActiveSyncJobBannerProps {
  /** ISO timestamp the BE returned for ``active_job_started_at``. */
  readonly startedAt?: string | null;
  /**
   * Cancel transport — when omitted the button is hidden (read-only
   * mounts e.g. the Integrazioni hub list row when the operator
   * lacks the per-connection permission).
   */
  readonly onCancel?: () => void | Promise<void>;
  /**
   * Drives the cancel-button label + spinner. ``"cancelling"`` keeps
   * the BE-side state machine visible while the cancellation
   * propagates (Temporal cancel can take 5–30 s before the job
   * terminates) — without this surface the operator would think the
   * click failed and try again, double-firing the 409 path.
   */
  readonly status?: ActiveSyncJobBannerStatus;
  /** Extra wrapper classes (responsive layout overrides etc.). */
  readonly className?: string;
  /** Stable DOM id so a sibling CTA can ``aria-describedby`` it. */
  readonly id?: string;
}

export const ActiveSyncJobBanner: React.FC<ActiveSyncJobBannerProps> = ({
  startedAt,
  onCancel,
  status = "running",
  className,
  id,
}) => {
  const { i18n } = useLingui();
  const containerRef = React.useRef<HTMLDivElement>(null);

  // Focus the banner on mount BUT only when no other element is
  // already focused. This prevents the focus-war with a hosting
  // Radix ``<Drawer>`` (or ``<Dialog>``) focus trap: the trap moves
  // focus to the first focusable on mount, and a naive ``focus()``
  // here would steal it back → jitter + double screen-reader
  // announcement (audit ciclo 3 CRIT-A1). Also avoids strapping the
  // focus away from a user mid-typing in an unrelated input when the
  // banner mounts async (e.g. after a WS event).
  React.useEffect(() => {
    if (typeof document === "undefined") return;
    const active = document.activeElement;
    const stealable =
      active === null ||
      active === document.body ||
      active === document.documentElement;
    if (stealable) {
      containerRef.current?.focus();
    }
  }, []);

  // Ticker that forces a re-render every 15 s so the
  // ``formatRelativePast`` output ("avviata 20s fa" → "avviata 35s
  // fa" → "avviata 1 min fa") stays honest as the banner ages.
  // Without it the banner froze at the first calculation and a
  // 5-min-old job kept reading as "pochi istanti fa" (audit ciclo 3
  // UX gap #1). ``startedAgo`` is computed inline on every render
  // (no ``useMemo``) so the linter is happy AND the tick naturally
  // recomputes — ``formatRelativePast`` is a few microseconds, not
  // worth memoising.
  const [, forceTick] = React.useReducer((n: number) => n + 1, 0);
  React.useEffect(() => {
    const id = window.setInterval(() => forceTick(), 15_000);
    return () => window.clearInterval(id);
  }, []);

  // Fall back to "pochi istanti fa" when ``startedAt`` is missing —
  // an active-job 409 we just received from the BE is by definition
  // a recent event, so the friendly fallback beats the cryptic
  // ``job 12abcd…`` previously shown (audit WARN-2).
  const startedAgoFallback = i18n._(msg`pochi istanti fa`);
  const startedAgo =
    formatRelativePast(startedAt, { fallback: startedAgoFallback }) ??
    startedAgoFallback;

  const isCancelling = status === "cancelling";

  return (
    // Outer wrapper is a plain ``<div>`` — it owns layout + focus
    // management but NOT the live region. The status message sits in
    // a dedicated inner ``role="status"`` block so screen readers
    // announce ONLY the message (and its updates), not the wrapper.
    // The cancel ``<button>`` is a sibling outside the live region:
    // toggling its ``disabled``/``aria-busy`` would otherwise re-fire
    // the announcement on every state change (audit CRIT-1).
    <div
      ref={containerRef}
      tabIndex={-1}
      id={id}
      className={cn(
        "flex w-full flex-col gap-2 rounded-lg border border-hairline border-l-2 border-l-ando-orange/55 bg-card/60 px-4 py-3 sm:flex-row sm:items-center",
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/45",
        className,
      )}
    >
      <span
        aria-hidden
        className="h-2 w-2 shrink-0 animate-pulse rounded-full bg-ando-orange motion-reduce:animate-none"
        style={{ animationDuration: "1.6s" }}
      />
      {/* ``<output>`` has implicit ``role="status"`` + ``aria-live="polite"``
          per the HTML spec — preferred semantic element vs a raw
          ``<div role="status">``. Audit CRIT-1's concern (double-
          announce when the cancel button toggles its disabled state)
          no longer applies because the button is a sibling OUTSIDE
          this live region; only the message text mutates here. */}
      <output
        aria-atomic="true"
        className="flex min-w-0 flex-1 items-center gap-2"
      >
        <Loader2
          aria-hidden
          className="h-4 w-4 shrink-0 animate-spin text-ando-orange motion-reduce:animate-none"
        />
        <div className="flex min-w-0 flex-col">
          <span className="font-medium text-[12.5px] text-ink-1">
            {isCancelling
              ? i18n._(msg`Annullamento in corso…`)
              : i18n._(msg`Sincronizzazione già in corso`)}
          </span>
          <span className="truncate text-[11px] text-ink-3">
            {isCancelling
              ? i18n._(msg`Attendi qualche secondo, poi riprova.`)
              : i18n._(msg`avviata ${startedAgo}`)}
          </span>
        </div>
      </output>
      {onCancel ? <CancelButton onCancel={onCancel} status={status} /> : null}
    </div>
  );
};
ActiveSyncJobBanner.displayName = "ActiveSyncJobBanner";

const CancelButton: React.FC<{
  readonly onCancel: () => void | Promise<void>;
  readonly status: ActiveSyncJobBannerStatus;
}> = ({ onCancel, status }) => {
  const { i18n } = useLingui();
  const isCancelling = status === "cancelling";
  return (
    <button
      type="button"
      onClick={() => void onCancel()}
      disabled={isCancelling}
      aria-busy={isCancelling || undefined}
      className={cn(
        "inline-flex shrink-0 items-center gap-1.5 self-start rounded-md border border-ando-orange/45 bg-white/85 px-3 py-1.5 font-medium text-[12px] text-ando-orange transition-colors sm:self-auto",
        "hover:bg-ando-orange hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/45",
        "disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-white/85 disabled:hover:text-ando-orange",
      )}
    >
      {isCancelling ? (
        <Loader2 className="h-3 w-3 animate-spin motion-reduce:animate-none" aria-hidden />
      ) : (
        <X className="h-3 w-3" aria-hidden />
      )}
      {isCancelling ? i18n._(msg`Annullamento…`) : i18n._(msg`Annulla sync`)}
    </button>
  );
};
